/**
 * ============================================================================
 * EXPORT ANALYTICS COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Component for exporting analytics data
 */

import React, { useState } from 'react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Button from '../common/Button';
import Select from '../common/Select';
import Modal from '../common/Modal';
import Card from '../common/Card';

/**
 * Export Analytics Component
 * @param {Object} props
 * @param {string} props.analyticsType - Type of analytics to export
 * @param {Object} props.filters - Current filters
 * @returns {JSX.Element}
 */
const ExportAnalytics = ({ analyticsType = 'transactions', filters = {} }) => {
  const { exportAnalyticsData, loading } = useAnalytics();
  const { showError, showSuccess } = useToast();
  const { language } = useI18n();
  const [showModal, setShowModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');

  const handleExport = async () => {
    try {
      await exportAnalyticsData(analyticsType, {
        ...filters,
        format: exportFormat,
      });
      showSuccess(
        language === 'fr'
          ? 'Export réussi! Téléchargement en cours...'
          : 'Export successful! Download starting...'
      );
      setShowModal(false);
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec de l\'export' : 'Export failed')
      );
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowModal(true)}
        className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20"
      >
        📥 {language === 'fr' ? 'Exporter' : 'Export'}
      </Button>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={language === 'fr' ? 'Exporter les Données' : 'Export Data'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'fr' ? 'Format d\'export' : 'Export Format'}
            </label>
            <Select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              options={[
                { value: 'csv', label: 'CSV' },
                { value: 'json', label: 'JSON' },
                { value: 'xlsx', label: 'Excel (XLSX)' },
              ]}
            />
          </div>
          <Card variant="outlined" className="p-4 bg-blue-50 dark:bg-blue-900/20">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {language === 'fr'
                ? `Les données ${analyticsType} seront exportées dans le format sélectionné avec les filtres actuels.`
                : `${analyticsType} data will be exported in the selected format with current filters.`}
            </p>
          </Card>
          <div className="flex gap-2 pt-4">
            <Button
              variant="gradient"
              onClick={handleExport}
              loading={loading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-green-600 shadow-xl hover:shadow-2xl transition-all"
            >
              📥 {language === 'fr' ? 'Exporter' : 'Export'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              className="flex-1"
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ExportAnalytics;
